import * as z from "zod";
import { userFacingError } from "../authorization";
import { copyAllCommitFiles } from "../commitFiles";
import { zql } from "../generatedDrizzleSchema";
import { assertSuppliersAvailableToDependent } from "../rbac/crossResourceAvailability";
import { creatorColumns } from "../rbac/roles";
import { mutator } from "./mutator";
import { assertNotGitSynced } from "./syncGit";

export const restoreToCommit = mutator({
  schema: z.object({
    branchId: z.string(),
    targetCommitId: z.string(),
    newCommitId: z.string(),
    chatId: z.string(),
    entryId: z.string(),
    restoredFromMessage: z.string().nullable(),
  }),
  resolve: {
    branch: { kind: "branch", from: "branchId" },
    targetCommit: { kind: "commit", from: "targetCommitId" },
  },
  permissions: [
    {
      // Restoring main changes the live workflow, so it needs publish rights.
      permission: ({ args }) =>
        args.branch.name === "main" ? "space:push_live" : "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      newCommitId,
      chatId: newChatId,
      entryId,
      restoredFromMessage,
      branch,
      targetCommit,
    },
  }) => {
    const creator = creatorColumns(ctx);
    const now = Date.now();

    if (targetCommit.branch.workflowId !== branch.workflowId) {
      throw userFacingError("Commit not found");
    }

    if (!branch.headCommitId) {
      throw userFacingError("Branch has no head commit");
    }

    if (branch.name === "main") {
      await assertNotGitSynced(tx, branch.workflow.spaceId);
    }

    const appUsages = await tx.run(
      zql.connectedAppUsages
        .where("type", "commit_step")
        .where("refId", targetCommit.id)
        .related("connectedApp")
    );
    await assertSuppliersAvailableToDependent(
      tx,
      ctx.tenantId,
      "space_uses_connected_apps",
      branch.workflow.spaceId,
      appUsages.map((u) => ({
        id: u.connectedAppId,
        label: u.connectedApp!.name,
      })),
      "restore workflow"
    );

    // Check if the current head commit has a message (is already a checkpoint)
    const [currentHead] = await tx.run(
      zql.workflowCommits.where("id", branch.headCommitId)
    );

    // If not already a checkpoint, save current state so user can undo the restore
    if (currentHead && !currentHead.message) {
      await tx.mutate.workflowCommits.update({
        id: branch.headCommitId,
        message: "Before restore",
      });
    }

    // Create new checkpoint as a clone of the target
    await tx.mutate.workflowCommits.insert({
      id: newCommitId,
      branchId: branch.id,
      parentCommitId: branch.headCommitId,
      chatId: newChatId,
      message: restoredFromMessage,
      createdAt: now,
      ...creator,
      tenantId: ctx.tenantId,
    });

    // Copy all files from target commit to new commit
    await copyAllCommitFiles(
      { tx, commitId: targetCommit.id, tenantId: ctx.tenantId },
      newCommitId
    );

    // Update branch head
    await tx.mutate.workflowBranches.update({
      id: branch.id,
      headCommitId: newCommitId,
    });

    // Create a new chat on the new commit with a restore message
    await tx.mutate.chats.insert({
      id: newChatId,
      commitId: newCommitId,
      createdAt: now,
      lastChatEntryType: "SystemMessage",
      tenantId: ctx.tenantId,
    });

    const text = restoredFromMessage
      ? `Restored to: “${restoredFromMessage}”`
      : "Restored to previous version";

    await tx.mutate.chatEntries.insert({
      id: entryId,
      chatId: newChatId,
      entryType: "SystemMessage",
      createdAt: now,
      previousEntryId: null,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.systemMessages.insert({
      entryId,
      text,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowBranches.update({
      id: branch.id,
      lastChatEntryAt: now,
      lastChatEntryType: "SystemMessage",
      awaitingAction: false,
    });

    await tx.mutate.workflows.update({
      id: branch.workflowId,
      updatedAt: now,
    });
  },
});
